import { Link } from "react-router-dom";
import { ArrowLeft, Home } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Header */}
      <nav className="bg-[#0f172a] border-b border-slate-800 px-4 md:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold">Thames Mortgage & Protection</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <h1 className="text-3xl font-bold mb-2">Privacy Notice</h1>
        <p className="text-slate-400 mb-8">Last Updated: April 3, 2024</p>
        
        <div className="prose prose-invert prose-slate max-w-none space-y-6 text-slate-300">
          <h2 className="text-xl font-semibold text-white">Who We Are</h2>
          <p>
            Thames Mortgage & Protection Ltd is registered in England and Wales No. 16801243. Registered Office: 
            Unit 4.1, Town Quay Wharf, Abbey Road, Barking, IG11 7BZ. Thames Mortgage & Protection Ltd is authorised 
            and regulated by the Financial Conduct Authority FRN 1045227.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">What Data We Collect</h2>
          <p>We may collect, store and use the following kinds of personal data:</p>
          
          <h3 className="text-lg font-medium text-white mt-4">Basic Personal Information</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Title, name, date of birth, gender, nationality, marital status</li>
            <li>Contact details including email, phone numbers, addresses</li>
            <li>Identity verification documents</li>
          </ul>

          <h3 className="text-lg font-medium text-white mt-4">Employment & Financial Information</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Employment details and remuneration</li>
            <li>Bank account information</li>
            <li>Tax information, loans, credit history</li>
            <li>Income and expenditure details</li>
            <li>Family circumstances and dependents</li>
          </ul>

          <h3 className="text-lg font-medium text-white mt-4">Health & Sensitive Data</h3>
          <p>
            Where necessary for protection products, we may collect health status, treatment information, 
            and medical reports with your explicit consent.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">How We Collect Your Data</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Directly from you during meetings, conversations, and written correspondence</li>
            <li>From third parties such as credit reference agencies with your consent</li>
            <li>From employers for employment verification</li>
            <li>From public domain searches where permitted</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">Why We Process Your Data</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>To process your mortgage or protection application</li>
            <li>To provide you with marketing and special offers (with opt-out available)</li>
            <li>To monitor product performance for consumer outcomes</li>
            <li>To manage our relationship with you</li>
            <li>To respond to inquiries and defend against complaints</li>
            <li>For affordability checks in property transactions</li>
            <li>For business administration and website protection</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">Who We Share Data With</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Lenders and protection product suppliers</li>
            <li>IT providers and professional advisors</li>
            <li>Solicitors, conveyancers, estate agents</li>
            <li>Credit reference agencies</li>
            <li>Regulators (FCA, law enforcement) when required</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">Data Retention</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Successful applications: Full policy term + 6 years from completion</li>
            <li>Withdrawn/failed applications: 2-6 years depending on stage</li>
            <li>Enquiry data: 2 years from lead receipt</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Request erasure of your data</li>
            <li>Restrict processing</li>
            <li>Object to processing</li>
            <li>Data portability</li>
          </ul>
          <p className="mt-4">
            To exercise any of these rights, please contact us at <strong>admin@thamesresi.com</strong> or 
            write to us at Unit 4.1, Town Quay Wharf, Abbey Road, Barking, IG11 7BZ. We will respond within one month.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">Contact the ICO</h2>
          <p>
            If you have concerns about how we handle your data, you can contact the Information Commissioner's 
            Office at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">ico.org.uk</a>.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center text-slate-500 text-sm">
          <p>© 2026 Thames Mortgage & Protection Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
